import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { playNotificationDing } from "../lib/notificationSound";
import {
  ChartLine, Kanban, Ticket, BookOpen, Users, Gauge, Gear, Buildings,
  SignOut, Monitor, PlusCircle, HardDrives, Lightning, FileText, RocketLaunch, Warning,
  Signature as SigIcon, TrashSimple, Star, ChartBar, CaretLeft, CaretRight, Robot, Question, Bug, Megaphone, Info,
  List as ListIcon, X as CloseIcon,
} from "@phosphor-icons/react";
import NotificationBell from "./NotificationBell";

// Base staff links — visible to both techs and admins.
// Entries flagged techOnly:true are HIDDEN for admins (they access those features
// via the right-hand Paramètres tabs — Modules and Signaler un bug).
// Entries with `moduleId` are only shown to techs when that module is enabled
// on /modules (admins always see them).
const staffLinks = [
  { to: "/dashboard", label: "Tableau de bord", icon: ChartLine },
  { to: "/tickets", label: "Mes Tickets", icon: Ticket, bold: true },
  { to: "/kanban", label: "Kanban", icon: Kanban },
  { to: "/inventaire", label: "Inventaire", icon: HardDrives, flag: "inventory" },
  { to: "/macros", label: "Macros", icon: Lightning },
  { to: "/ia-netkom", label: "Assistant IA", icon: Robot },
  { to: "/favoris", label: "Favoris", icon: Star },
  { to: "/statistiques-techs", label: "Stats techniciens", icon: ChartBar },
  { to: "/societes", label: "Sociétés", icon: Buildings },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Users },
  { to: "/contrats", label: "Contrats", icon: FileText, moduleId: "contracts" },
  { to: "/modules", label: "Modules", icon: RocketLaunch, techOnly: true },
  { to: "/kb", label: "Base de connaissances", icon: BookOpen },
  { to: "/signaler-un-bug", label: "Signaler un bug", icon: Bug, techOnly: true },
  { to: "/a-propos", label: "À propos", icon: Info, techOnly: true },
];

const adminLinks = [
  { to: "/automations", label: "Automatisations", icon: RocketLaunch },
  { to: "/modules", label: "Modules", icon: RocketLaunch },
  { to: "/parametres", label: "Paramètres", icon: Gear },
];

const clientLinks = [
  { to: "/mes-tickets", label: "Mes demandes", icon: Ticket },
  { to: "/nouvelle-demande", label: "Nouvelle demande", icon: PlusCircle },
  { to: "/mes-services", label: "Mes services", icon: FileText },
  { to: "/inventaire", label: "Mon matériel", icon: HardDrives, flag: "inventory" },
  { to: "/kb", label: "Base de connaissances", icon: BookOpen },
  { to: "/documentation", label: "Documentation", icon: Question },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [newCount, setNewCount] = useState(0);
  const [aiCount, setAiCount] = useState(0);
  const prevCountRef = useRef(null);
  const prevUnreadRef = useRef(null);
  const prevClientMaxRef = useRef(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("sidebar_collapsed", next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };

  // v260 — Mobile drawer state. On < md screens, the sidebar becomes a
  // slide-in drawer overlaid on top of the page, opened via a hamburger
  // button in a top mobile bar. Closes on : link click, backdrop tap, Escape.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    // Close the drawer on route change so nav feels natural.
    setMobileOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    // Lock body scroll while the drawer is open, and close on Escape.
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  // Staff: poll count of "nouveau" tickets · Client: poll for updates on their own tickets.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const loadStaff = async () => {
      try {
        // 1) Count "untaken" tickets for the red badge — exclude AI calls (they have their own green badge alongside)
        const r = await api.get("/tickets", { params: { taken: "no", hide_ai: "true", per_page: 1 } });
        if (cancelled) return;
        const total = Number(r.data?.total || 0);
        setNewCount(total);
        // 2) Count unread tickets (= client replies awaiting staff) — play ding on increase
        try {
          const r2 = await api.get("/tickets", { params: { unread: "only", per_page: 1 } });
          if (cancelled) return;
          const unreadTotal = Number(r2.data?.total || 0);
          if (prevUnreadRef.current !== null && unreadTotal > prevUnreadRef.current) {
            playNotificationDing();
          }
          prevUnreadRef.current = unreadTotal;
        } catch { /* ignore */ }
        // 3) Count active AI tickets (Flux IA, excluding closed) for the green badge — v286 : now shown alongside the red one on "Mes Tickets" instead of on Kanban.
        try {
          const r3 = await api.get("/tickets", { params: { ai: "only", hide_closed: "true", per_page: 1 } });
          if (cancelled) return;
          setAiCount(Number(r3.data?.total || 0));
        } catch { /* ignore */ }
        prevCountRef.current = total;
      } catch { /* ignore */ }
    };

    const loadClient = async () => {
      try {
        // Ping the client's own tickets and detect any activity newer than what we've seen.
        const r = await api.get("/tickets", { params: { per_page: 50 } });
        if (cancelled) return;
        const items = r.data?.items || r.data?.data || [];
        // Consider updated_at (or created_at) to detect activity — new comment updates it.
        let max = 0;
        for (const t of items) {
          const ts = Date.parse(t.updated_at || t.created_at || 0) || 0;
          if (ts > max) max = ts;
        }
        if (prevClientMaxRef.current !== null && max > prevClientMaxRef.current) {
          playNotificationDing();
        }
        prevClientMaxRef.current = max;
      } catch { /* ignore */ }
    };

    const load = user.role === "client" ? loadClient : loadStaff;
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  // v276 — Fetch modules state on mount + refresh every 60s to hide the
  // sidebar entry when its module is disabled (e.g. « Mur des urgences »).
  const [enabledModuleIds, setEnabledModuleIds] = useState(null); // null until loaded → treat as "unknown, hide gated"
  useEffect(() => {
    if (!user || user.role === "client") { setEnabledModuleIds(new Set()); return; }
    let cancelled = false;
    const loadMods = async () => {
      try {
        const r = await api.get("/modules");
        if (cancelled) return;
        const mods = r.data?.modules || r.data || [];
        const enabled = new Set(
          mods.filter((m) => (user.role === "admin" ? m.enabled !== false : m.enabled_for_tech)).map((m) => m.id)
        );
        setEnabledModuleIds(enabled);
      } catch {
        if (!cancelled) setEnabledModuleIds(new Set());
      }
    };
    loadMods();
    const t = setInterval(loadMods, 60000);
    window.addEventListener("module-flags-changed", loadMods);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("module-flags-changed", loadMods); };
  }, [user]);

  const [flags, setFlags] = useState(null);
  useEffect(() => {
    if (!user) { return undefined; }
    let cancelled = false;
    const loadFlags = () => api.get("/modules/flags").then((r) => { if (!cancelled) setFlags(r.data); }).catch(() => {});
    loadFlags();
    const t = setInterval(loadFlags, 60000);
    window.addEventListener("module-flags-changed", loadFlags);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("module-flags-changed", loadFlags); };
  }, [user]);

  const filterByModule = (l) => {
    if (l.flag && (flags === null || flags[l.flag] === false)) return false;
    if (!l.moduleId) return true;
    if (user?.role === "admin") return enabledModuleIds === null || enabledModuleIds.has(l.moduleId);
    if (enabledModuleIds === null) return false;
    return enabledModuleIds.has(l.moduleId);
  };

  const links =
    user?.role === "client"
      ? clientLinks.filter(filterByModule)
      : user?.role === "admin"
        ? [...staffLinks.filter((l) => !l.techOnly).filter(filterByModule), ...adminLinks]
        : staffLinks.filter(filterByModule);

  return (
    <>
      {/* v260 — Mobile top bar (visible < md). Contains hamburger + brand +
          bell for staff. Fixed so it stays available while scrolling. */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-[hsl(222_25%_9%)] text-white flex items-center justify-between px-3 border-b border-white/10 shadow-sm"
        data-testid="mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-md hover:bg-white/10 relative"
          data-testid="mobile-menu-btn"
          aria-label="Ouvrir le menu"
        >
          <ListIcon size={22} weight="bold"/>
          {(newCount > 0 || aiCount > 0) && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-[hsl(222_25%_9%)] animate-pulse" aria-hidden />
          )}
        </button>
        <Link to={user?.role === "client" ? "/mes-tickets" : "/dashboard"}
              className="flex items-center gap-2" data-testid="mobile-brand-link">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <Gauge weight="bold" size={16} className="text-white"/>
          </div>
          <span className="font-display font-bold text-sm">MasterTicket</span>
        </Link>
        <div className="flex items-center">
          {user?.role !== "client" && <NotificationBell/>}
        </div>
      </header>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-backdrop"
          aria-hidden
        />
      )}

    <aside
      className={cn(
        // Desktop : sticky sidebar with collapsed/expanded widths
        "flex-col shrink-0 border-r bg-[hsl(222_25%_9%)] text-[hsl(210_20%_92%)]",
        "md:flex md:h-screen md:sticky md:top-0 md:transition-[width] md:duration-200 md:ease-out",
        collapsed ? "md:w-[68px]" : "md:w-64",
        // Mobile : full-height drawer sliding from the left
        "fixed inset-y-0 left-0 z-50 w-72 max-w-[80vw] flex transition-transform duration-200 ease-out md:transition-none md:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
      )}
      data-testid="sidebar"
      data-collapsed={collapsed ? "1" : "0"}
      data-mobile-open={mobileOpen ? "1" : "0"}
    >
      <div className={cn("py-5 border-b border-white/10 flex items-center", collapsed ? "px-3 justify-center" : "px-6 justify-between")}>
        <Link to={user?.role === "client" ? "/mes-tickets" : "/dashboard"} className="flex items-center gap-2 min-w-0" data-testid="brand-link" title="MasterTicket">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shrink-0">
            <Gauge weight="bold" size={20} className="text-white" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-display font-bold text-white leading-tight truncate">MasterTicket</div>
              <div className="text-[0.65rem] text-white/50 tracking-wider uppercase">Support IT</div>
            </div>
          )}
        </Link>
        {/* v260 — Close button on mobile drawer */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="md:hidden p-1 rounded-md hover:bg-white/10 text-white/70 hover:text-white"
          data-testid="mobile-drawer-close"
          aria-label="Fermer le menu"
        >
          <CloseIcon size={20} weight="bold"/>
        </button>
        {!collapsed && (
          <button
            type="button"
            data-testid="sidebar-collapse-btn"
            onClick={toggleCollapsed}
            className="hidden md:block p-1 rounded-md hover:bg-white/10 text-white/60 hover:text-white shrink-0"
            title="Réduire le menu"
            aria-label="Réduire le menu"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
        )}
      </div>
      {collapsed && (
        <button
          type="button"
          data-testid="sidebar-expand-btn"
          onClick={toggleCollapsed}
          className="hidden md:flex mx-2 mt-2 py-1 rounded-md hover:bg-white/10 text-white/60 hover:text-white items-center justify-center"
          title="Agrandir le menu"
          aria-label="Agrandir le menu"
        >
          <CaretRight size={16} weight="bold" />
        </button>
      )}

      <nav className={cn("flex-1 py-4 space-y-1 overflow-y-auto overflow-x-hidden", collapsed ? "px-2" : "px-3")}>
        {links.map((l) => {
          const Icon = l.icon;
          const active = pathname === l.to || pathname.startsWith(l.to + "/");
          return (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "")}`}
              title={collapsed ? l.label : undefined}
              className={cn(
                "flex items-center gap-3 rounded-md text-sm transition-colors relative",
                collapsed ? "px-2 py-2 justify-center" : "px-3 py-2",
                active
                  ? "bg-white/10 text-white font-medium"
                  : "text-white/70 hover:bg-white/5 hover:text-white"
              )}
            >
              <Icon size={18} weight={active ? "fill" : "regular"} />
              {!collapsed && <span className={cn("flex-1 truncate", l.bold && "font-bold")}>{l.label}</span>}
              {l.to === "/tickets" && newCount > 0 && (
                <span
                  data-testid="sidebar-new-ticket-badge"
                  className={cn(
                    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ring-2 ring-[hsl(222_25%_9%)] animate-pulse",
                    collapsed ? "absolute -top-0.5 -right-0.5" : "ml-auto"
                  )}
                  title={`${newCount} ticket(s) à traiter (non attribué)`}
                >
                  {newCount > 99 ? "99+" : newCount}
                </span>
              )}
              {/* v286 — Badge vert « appels IA actifs » désormais collé au
                  bouton « Mes Tickets », juste à côté du badge rouge (au lieu
                  de sur /kanban). Quand les deux badges sont présents en
                  mode déplié, on garde le rouge avec ml-auto et on ajoute
                  ml-1 sur le vert pour un petit espacement propre. */}
              {l.to === "/tickets" && aiCount > 0 && (
                <span
                  data-testid="sidebar-ai-calls-badge"
                  className={cn(
                    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold leading-none ring-2 ring-[hsl(222_25%_9%)]",
                    collapsed
                      ? "absolute -bottom-0.5 -right-0.5"
                      : (newCount > 0 ? "ml-1" : "ml-auto")
                  )}
                  title={`${aiCount} appel(s) IA actif(s) — Kanban › Appels IA`}
                >
                  {aiCount > 99 ? "99+" : aiCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className={cn("py-3 border-t border-white/10", collapsed ? "px-2" : "px-3")}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <Link
              to="/ma-signature"
              data-testid="profile-avatar-link"
              title={`${user?.full_name || ""} — Mon profil`}
              className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-sm font-semibold overflow-hidden ring-0 hover:ring-2 hover:ring-blue-400/60 transition-shadow cursor-pointer"
            >
              {user?.photo_url
                ? <img src={user.photo_url} alt="" className="w-full h-full object-cover"/>
                : user?.full_name?.[0]?.toUpperCase()}
            </Link>
            {user?.role !== "client" && <NotificationBell/>}
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="p-2 rounded-md hover:bg-white/10 text-white/60 hover:text-white"
              title="Se déconnecter"
            >
              <SignOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-white/5">
            <Link
              to="/ma-signature"
              data-testid="profile-avatar-link"
              title="Mon profil"
              className="flex items-center gap-3 min-w-0 flex-1 group"
            >
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-sm font-semibold overflow-hidden shrink-0 ring-0 group-hover:ring-2 group-hover:ring-blue-400/60 transition-shadow">
                {user?.photo_url
                  ? <img src={user.photo_url} alt="" className="w-full h-full object-cover"/>
                  : user?.full_name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-white truncate group-hover:text-blue-300 transition-colors">{user?.full_name}</div>
                <div className="text-[0.68rem] text-white/50 uppercase tracking-wider">{user?.role}</div>
              </div>
            </Link>
            {user?.role !== "client" && <NotificationBell/>}
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="p-2 rounded-md hover:bg-white/10 text-white/60 hover:text-white"
              title="Se déconnecter"
            >
              <SignOut size={16} />
            </button>
          </div>
        )}
      </div>
    </aside>
    </>
  );
}
